import {
  MEAL_PATTERN_SLOTS,
  MEAL_PATTERN_STATUSES,
  type AdminMealPattern,
  type MealPatternSlotName,
} from '@steado/admin-contract/meal-patterns';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { useCallback, useId } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { ScopePills } from '@/components/table/ScopePills';
import { TONES } from '@/components/table/tone';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { humanise } from '@/lib/format';

import { PatternDrawer } from './PatternDrawer';
import { SlotPreviewPanel } from './SlotPreviewPanel';
import { mealPatternsQueryOptions } from './meal-patterns-query';
import { seededTarget, type MealPatternsSearch } from './meal-patterns-search';
import { PATTERN_STATUS_TONES, describeComponents, isLiveStatus } from './pattern-tone';

const ALL_SLOTS = '__all__';

export interface MealPatternsScreenProps {
  readonly search: MealPatternsSearch;
  readonly onSearchChange: (next: MealPatternsSearch) => void;
}

/**
 * The meal grammar, as a list of shapes.
 *
 * **Seeded shapes are listed too, and that is the point.** The database holds
 * nothing until somebody curates a shape, so a list of database rows alone was
 * a screen called Meal patterns that showed an operator nothing at all while
 * twelve patterns governed every generated meal. What is in force is what is
 * listed; where it came from is on the card.
 *
 * A seeded row cannot be edited, because there is no row to edit. Opening one
 * offers the only thing that would change it: writing a curated shape of the
 * same name, prefilled from what it says today.
 *
 * Cards rather than a table, because a shape is not a row of comparable
 * values — it is a small structured document, and the components strip is the
 * part an operator actually reads.
 */
export function MealPatternsScreen({ search, onSearchChange }: MealPatternsScreenProps) {
  const { api, can } = useAuth();
  const statusId = useId();

  /*
   * Fetched without the slot filter, and narrowed here.
   *
   * The whole grammar is a couple of dozen shapes, so one read answers both
   * "show me this slot" and "how many are in each slot" — and the pills need
   * the second. Status and history stay server-side: they change which rows
   * exist, not which of them to show.
   */
  const patterns = useQuery(mealPatternsQueryOptions(api, { ...search, slot: undefined }));
  const all = patterns.data ?? [];
  const shown = search.slot === undefined ? all : all.filter((row) => row.slot === search.slot);

  const onOpen = useCallback(
    (pattern: string | undefined) => {
      onSearchChange({ ...search, pattern });
    },
    [search, onSearchChange],
  );

  const onSelectSlot = useCallback(
    (slot: MealPatternSlotName | typeof ALL_SLOTS) => {
      onSearchChange({
        ...search,
        slot: slot === ALL_SLOTS ? undefined : slot,
        pattern: undefined,
      });
    },
    [search, onSearchChange],
  );

  if (patterns.isError) {
    return (
      <ErrorState
        title="Could not read the meal grammar"
        message={patterns.error.message}
        onRetry={() => {
          void patterns.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1200px] flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <ScopePills
          label="Slot"
          scopes={[
            { id: ALL_SLOTS, label: 'Every slot', count: all.length },
            ...MEAL_PATTERN_SLOTS.map((slot) => ({
              id: slot,
              label: humanise(slot),
              count: all.filter((row) => row.slot === slot).length,
            })),
          ]}
          value={search.slot ?? ALL_SLOTS}
          onChange={onSelectSlot}
        />

        <span className="ml-auto flex flex-wrap items-center gap-2">
          <Label htmlFor={statusId} className="sr-only">
            Status
          </Label>
          <Select
            value={search.status ?? ALL_SLOTS}
            onValueChange={(next) => {
              onSearchChange({
                ...search,
                status: next === ALL_SLOTS ? undefined : (next as MealPatternsSearch['status']),
                pattern: undefined,
              });
            }}
          >
            <SelectTrigger id={statusId} aria-label="Status" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SLOTS}>Any status</SelectItem>
              {MEAL_PATTERN_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {humanise(status)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={search.history === true}
            onClick={() => {
              onSearchChange({ ...search, history: search.history === true ? undefined : true });
            }}
            className={cn(
              search.history === true
                ? 'border-foreground bg-foreground text-background hover:bg-foreground hover:text-background'
                : 'text-muted-foreground',
            )}
          >
            Every version
          </Button>

          <Button
            type="button"
            size="sm"
            disabled={!can.mutate}
            title={can.mutate ? undefined : 'Editing the meal grammar requires the admin role.'}
            onClick={() => {
              onOpen('new');
            }}
          >
            <PlusIcon aria-hidden="true" className="size-3.5" />
            New pattern
          </Button>
        </span>
      </div>

      <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.45fr_1fr]">
        <div className="flex flex-col gap-3">
          {patterns.isPending ? (
            <p className="rounded-xl border border-border bg-card px-[18px] py-6 text-[13px] text-muted-foreground">
              Reading the grammar…
            </p>
          ) : shown.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-8 py-14 text-center">
              <h3 className="text-[13.5px] font-semibold text-foreground">Nothing matches that filter</h3>
              <p className="max-w-[440px] text-[13px] leading-relaxed text-muted-foreground">
                The shapes the API ships with are listed here alongside any you curate, so an empty list means
                the filter, not the grammar. Clear it to see what is in force.
              </p>
            </div>
          ) : (
            shown.map((pattern) => (
              <PatternCard
                key={pattern.id ?? seededTarget(pattern.slug)}
                pattern={pattern}
                onOpen={() => {
                  onOpen(pattern.id ?? seededTarget(pattern.slug));
                }}
              />
            ))
          )}
        </div>

        {search.slot === undefined ? (
          <div className="rounded-xl border border-border bg-card px-[18px] py-6 text-[13px] text-pretty text-muted-foreground">
            Pick a slot to see what its whole live grammar would reject. A meal has to satisfy every shape in
            its slot at once, so there is nothing to measure until you have chosen one.
          </div>
        ) : (
          <SlotPreviewPanel slot={search.slot} />
        )}
      </div>

      {search.pattern === undefined ? null : (
        <PatternDrawer
          patternId={search.pattern}
          onClose={() => {
            onOpen(undefined);
          }}
        />
      )}
    </div>
  );
}

/** One shape in the grammar, and everything that decides whether it applies. */
function PatternCard({
  pattern,
  onOpen,
}: {
  readonly pattern: AdminMealPattern;
  readonly onOpen: () => void;
}) {
  const live = isLiveStatus(pattern.status);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full cursor-pointer flex-col overflow-hidden rounded-xl border border-border bg-card text-left hover:border-input"
    >
      <div className="flex w-full items-start gap-2.5 px-[18px] pt-3.5 pb-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-mono text-[13px] font-medium text-foreground">{pattern.slug}</span>
          <span className="text-xs text-muted-foreground">{originOf(pattern)}</span>
        </span>
        <span className="ml-auto flex shrink-0 flex-col items-end gap-0.5">
          <span
            className={cn(
              'inline-flex h-5 items-center rounded-[10px] px-[7px] text-xs font-medium',
              TONES[PATTERN_STATUS_TONES[pattern.status]],
            )}
          >
            {humanise(pattern.status)}
          </span>
          {/* What the operator is actually here to learn: whether generation
              reads this shape right now. */}
          <span className={cn('text-xs', live ? 'text-ok-fg' : 'text-muted-foreground')}>
            {live ? 'in force' : 'not in force'}
          </span>
        </span>
      </div>

      <div className="flex w-full flex-wrap gap-1.5 border-t border-border px-[18px] py-3">
        {pattern.components.length === 0 ? (
          <span className="text-[12.5px] text-muted-foreground">
            No components — this shape matches nothing.
          </span>
        ) : (
          pattern.components.map((component) => (
            <span
              key={`${component.role}-${String(component.min)}-${String(component.max)}`}
              className={cn(
                'inline-flex items-center gap-[7px] rounded-xl border border-border px-[9px] py-[5px] text-[12.5px]',
                // Optional components sit back: the required ones are what a
                // meal has to have, and they should read first.
                component.required ? 'bg-secondary text-foreground' : 'bg-card text-muted-foreground',
              )}
            >
              {component.role === 'any_dish' ? 'dish' : component.role.replace(/_/g, ' ')}
              <span className="font-mono text-xs text-muted-foreground">
                {component.min === component.max
                  ? String(component.min)
                  : `${String(component.min)}-${String(component.max)}`}
              </span>
            </span>
          ))
        )}
      </div>

      <dl className="grid w-full grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-3 gap-y-1.5 border-t border-border px-[18px] py-2.5 text-[13px]">
        <dt className="text-muted-foreground">Shape</dt>
        <dd className="text-foreground">{describeComponents(pattern.components)}</dd>

        <dt className="text-muted-foreground">Diets</dt>
        <dd className="text-foreground">{pattern.dietVariants.map((diet) => humanise(diet)).join(', ')}</dd>

        <dt className="text-muted-foreground">Cuisines</dt>
        <dd className="text-foreground">
          {pattern.cuisines.length === 0 ? 'Any' : pattern.cuisines.map((one) => humanise(one)).join(', ')}
        </dd>

        <dt className="text-muted-foreground">Recurrence</dt>
        <dd className="text-foreground">
          {pattern.maxPerWeek === null
            ? 'No limit stated'
            : // Stated to the model, never enforced: a week-scoped violation has
              // nowhere to go in the repair pipeline.
              `At most ${String(pattern.maxPerWeek)} a week, as advice to the model`}
        </dd>

        <dt className="text-muted-foreground">Signed off by</dt>
        <dd className={pattern.reviewer === null ? 'text-warn-fg' : 'text-foreground'}>
          {pattern.reviewer ?? 'Nobody has reviewed this'}
        </dd>
      </dl>
    </button>
  );
}

/** Where this shape came from, and which version of it this is. */
function originOf(pattern: AdminMealPattern): string {
  if (pattern.origin === 'seeded') return 'Ships with the API — there is no row to edit';
  return `v${String(pattern.version ?? 0)} · ${pattern.createdBy}`;
}
